const { registerBlockType } = wp.blocks;
const {
  InspectorControls,
  MediaUpload,
  MediaUploadCheck,
  RichText,
  ColorPalette,
} = wp.blockEditor;
const { PanelBody, SelectControl, TextControl, Button, ToggleControl } =
  wp.components;
const { Fragment, useState } = wp.element;
const { __ } = wp.i18n;

import PgButton from '../utils/pg-button';

// Register the custom Gutenberg block
registerBlockType('pg/banner', {
  title: __('PG Single Banner', 'pg'),
  icon: 'format-image',
  category: 'layout',
  attributes: {
    headline: {
      type: 'string',
      default: '',
    },
    title: {
      type: 'string',
      default: '',
    },
    buttonText: {
      type: 'string',
      default: '',
    },
    buttonClass: {
      type: 'string',
      default: 'btn btn-dark btn-solid',
    },
    link: {
      type: 'string',
      default: '#',
    },
    sectionClass: {
      type: 'string',
      default: 'section-dark-primary',
    },
    bannerType: {
      type: 'string',
      default: 'wide',
    },
    backgroundImage: {
      type: 'string',
      default: '',
    },
    enableSection: {
      type: 'boolean',
      default: true,
    },
    borderRadiusClass: {
      type: 'string',
      default: '',
    },
    headlineColor: {
      type: 'string',
      default: '',
    },
    titleColor: {
      type: 'string',
      default: '',
    },
    buttonColor: {
      type: 'string',
      default: '',
    },
    cornerPositionClass: {
      type: 'string',
      default: 'corner-disabled',
    },
    cornerBackgroundClass: {
      type: 'string',
      default: 'corner-on-light-primary',
    },
  },

  edit: ({ attributes, setAttributes }) => {
    const {
      headline,
      title,
      buttonText,
      link,
      buttonClass,
      sectionClass,
      bannerType,
      backgroundImage,
      enableSection,
      borderRadiusClass,
      headlineColor,
      titleColor,
      buttonColor,
      cornerPositionClass,
      cornerBackgroundClass,
    } = attributes;

    const [isEditingImage, setIsEditingImage] = useState(false);

    const onImageSelect = (media) => {
      setAttributes({ backgroundImage: media.url });
      setIsEditingImage(false);
    };

    const bannerEdit = (
      <div
        className={`pg-banner pg-banner--${bannerType} ${borderRadiusClass}`}
        style={{
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="pg-banner--content">
          <RichText
            tagName="strong"
            className="pg-banner--headline"
            value={headline}
            onChange={(value) => setAttributes({ headline: value })}
            placeholder={__('Headline...', 'pg')}
            style={headlineColor ? { color: headlineColor } : {}}
          />
          <RichText
            tagName="h2"
            className="pg-banner--title"
            value={title}
            onChange={(value) => setAttributes({ title: value })}
            placeholder={__('Title...', 'pg')}
            style={titleColor ? { color: titleColor } : {}}
          />
          <PgButton
            link={link}
            label={buttonText}
            buttonClass={buttonClass}
            additionalClass="pg-banner--button"
            onUpdate={(object) => setAttributes({ ...attributes, ...object })}
            style={buttonColor ? { color: buttonColor } : {}}
            linkField="link"
            labelField="buttonText"
            buttonClassField="buttonClass"
          />
        </div>
      </div>
    );

    return (
      <Fragment>
        <InspectorControls>
          <PanelBody title={__('Section Settings', 'pg')} initialOpen={true}>
            <ToggleControl
              label={__('Enable Section', 'pg')}
              checked={enableSection}
              onChange={(value) => setAttributes({ enableSection: value })}
            />
            {enableSection && (
              <>
                <SelectControl
                  label={__('Section Class', 'pg')}
                  value={sectionClass}
                  options={[
                    { label: 'Dark Primary', value: 'section-dark-primary' },
                    {
                      label: 'Dark Secondary',
                      value: 'section-dark-secondary',
                    },
                    { label: 'Light Primary', value: 'section-light-primary' },
                    {
                      label: 'Light Secondary',
                      value: 'section-light-secondary',
                    },
                  ]}
                  onChange={(value) => setAttributes({ sectionClass: value })}
                />
                <SelectControl
                  label={__('Corner Position')}
                  value={cornerPositionClass}
                  options={[
                    { label: 'None', value: 'corner-disabled' },
                    { label: 'Left-Top', value: 'corner-left-top' },
                    { label: 'Left-Bottom', value: 'corner-left-bottom' },
                    { label: 'Right-Top', value: 'corner-right-top' },
                    { label: 'Right-Bottom', value: 'corner-right-bottom' },
                  ]}
                  onChange={(value) =>
                    setAttributes({ cornerPositionClass: value })
                  }
                />
                <SelectControl
                  label={__('Corner Background')}
                  value={cornerBackgroundClass}
                  options={[
                    { label: 'Dark Primary', value: 'corner-on-dark-primary' },
                    {
                      label: 'Dark Secondary',
                      value: 'corner-on-dark-secondary',
                    },
                    {
                      label: 'Light Primary',
                      value: 'corner-on-light-primary',
                    },
                    {
                      label: 'Light Secondary',
                      value: 'corner-on-light-secondary',
                    },
                  ]}
                  onChange={(value) =>
                    setAttributes({ cornerBackgroundClass: value })
                  }
                />
              </>
            )}
            <SelectControl
              label={__('Banner Type', 'pg')}
              value={bannerType}
              options={[
                { label: 'Wide', value: 'wide' },
                { label: 'Full', value: 'full' },
              ]}
              onChange={(value) => setAttributes({ bannerType: value })}
            />
          </PanelBody>

          <PanelBody title={__('Headline Color Settings')}>
            <ColorPalette
              value={headlineColor}
              onChange={(color) => setAttributes({ headlineColor: color })}
            />
          </PanelBody>
          <PanelBody title={__('Title Color Settings')}>
            <ColorPalette
              value={titleColor}
              onChange={(color) => setAttributes({ titleColor: color })}
            />
          </PanelBody>
          <PanelBody title={__('Button Color Settings')}>
            <ColorPalette
              value={buttonColor}
              onChange={(color) => setAttributes({ buttonColor: color })}
            />
          </PanelBody>

          {/* Border Radius Settings */}
          <PanelBody
            title={__('Border Radius Settings', 'pg')}
            initialOpen={true}
          >
            <SelectControl
              label={__('Border Radius Class', 'pg')}
              value={borderRadiusClass}
              options={[
                { label: 'Top Left Rounded', value: 'rounded-tl-64' },
                { label: 'Bottom Right Rounded', value: 'rounded-br-64' },
                { label: 'Top Right Rounded', value: 'rounded-tr-64' },
                { label: 'Bottom Left Rounded', value: 'rounded-bl-64' },
                { label: 'None', value: '' },
              ]}
              onChange={(value) => setAttributes({ borderRadiusClass: value })}
            />
          </PanelBody>

          {/* Background Image */}
          <PanelBody title={__('Background Image', 'pg')} initialOpen={true}>
            <MediaUploadCheck>
              {backgroundImage ? (
                <div style={{ marginBottom: '10px' }}>
                  <img
                    src={backgroundImage}
                    alt="Background"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'cover',
                      marginBottom: '10px',
                    }}
                  />
                  <Button
                    isDestructive
                    onClick={() => setAttributes({ backgroundImage: '' })}
                  >
                    {__('Remove Image', 'pg')}
                  </Button>
                </div>
              ) : (
                <MediaUpload
                  onSelect={onImageSelect}
                  allowedTypes={['image']}
                  render={({ open }) => (
                    <Button onClick={open} isPrimary>
                      {__('Select Background Image', 'pg')}
                    </Button>
                  )}
                />
              )}
            </MediaUploadCheck>
          </PanelBody>
        </InspectorControls>

        {/* Preview in Editor */}
        {enableSection ? (
          <section className={`section ${sectionClass}`}>
            <div
              className={`corner ${cornerPositionClass} ${sectionClass.replace(
                'section',
                'corner'
              )} ${cornerBackgroundClass}`}
            ></div>
            <div className="section-wrapper">{bannerEdit}</div>
          </section>
        ) : (
          bannerEdit
        )}
      </Fragment>
    );
  },

  save: ({ attributes }) => {
    const {
      headline,
      title,
      buttonText,
      link,
      buttonClass,
      sectionClass,
      bannerType,
      backgroundImage,
      enableSection,
      borderRadiusClass,
      headlineColor,
      titleColor,
      buttonColor,
      cornerPositionClass,
      cornerBackgroundClass,
    } = attributes;

    const bannerContent = (
      <div
        className={`pg-banner pg-banner--${bannerType} ${borderRadiusClass}`}
        style={{
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        }}
      >
        <div className="pg-banner--content">
          <RichText.Content
            tagName="strong"
            className={`pg-banner--headline`}
            value={headline}
            style={headlineColor ? { color: headlineColor } : {}}
          />
          <RichText.Content
            tagName="h2"
            className={`pg-banner--title`}
            value={title}
            style={titleColor ? { color: titleColor } : {}}
          />
          <a
            href={link}
            className={`${buttonClass} pg-banner--button`}
            style={buttonColor ? { color: buttonColor } : {}}
          >
            {buttonText}
          </a>
        </div>
      </div>
    );

    return enableSection ? (
      <section className={`section ${sectionClass}`}>
        <div
          className={`corner ${cornerPositionClass} ${sectionClass.replace(
            'section',
            'corner'
          )} ${cornerBackgroundClass}`}
        ></div>
        <div className="section-wrapper">{bannerContent}</div>
      </section>
    ) : (
      bannerContent
    );
  },
});
